import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    makeStyles,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from "@material-ui/core"
import { useSnackbar } from "notistack"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Redirect, useHistory } from "react-router-dom"
import { buscarTokenWhatsappOficial } from "../../services/comum"
import {
    buildWhatsappSessionContextKey,
    clearWhatsappOfficialSession,
    loadWhatsappOfficialSession,
    saveWhatsappOfficialSession,
} from "../../utils/whatsappOfficialSession"
import {
    countTemplateTextParameters,
    fetchWhatsappMessageTemplates,
    getWhatsappGraphToken,
    normalizeWhatsappPhone,
    sendWhatsappTemplateMessage,
    templateLanguageCode,
    templateOptionKey,
} from "../../services/whatsappGraphTemplate"
import useAppStore from "../../stores/AppStore"
import Cabecalho from "../Cabecalho"

const useStyles = makeStyles(theme => ({
    root: {
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        paddingTop: theme.spacing(10),
        padding: theme.spacing(2),
        paddingBottom: theme.spacing(4),
        marginLeft: 80,
        width: "calc(100% - 80px)",
        boxSizing: "border-box",
        [theme.breakpoints.down("md")]: {
            marginLeft: 0,
            width: "100%",
        },
    },
    paper: {
        maxWidth: 720,
        margin: "0 auto",
        padding: theme.spacing(3),
    },
    field: {
        marginBottom: theme.spacing(2),
        width: "100%",
    },
    actions: {
        marginTop: theme.spacing(2),
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        flexWrap: "wrap",
    },
    hint: {
        marginTop: theme.spacing(2),
        color: theme.palette.text.secondary,
        fontSize: "0.875rem",
    },
    warn: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1.5),
        backgroundColor: "#fff3e0",
        borderRadius: theme.shape.borderRadius,
        color: "#e65100",
    },
    credBox: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: "#fff",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        "& code": {
            fontSize: "0.9rem",
            wordBreak: "break-all",
        },
    },
}))

export default function EnvioTemplateWhatsappGrupo() {
    const classes = useStyles()
    const history = useHistory()
    const { enqueueSnackbar } = useSnackbar()
    const configuracoes = useAppStore(s => s.configuracoes)
    const userId = configuracoes?.user?.id
    const perfil = configuracoes?.user?.perfil
    const perfilPodeWhatsappTemplates =
        perfil === "CLIENTEFIEL" || perfil === "WHATSAPP"
    const idEstabelecimento = configuracoes?.user?.parametros?.estabelecimento?.id

    const tokenRef = useRef(null)

    const [telefonesRaw, setTelefonesRaw] = useState("")
    const [contaWhatsapp, setContaWhatsapp] = useState("")
    const [wabaidGraph, setWabaidGraph] = useState("")
    const [telefoneWhatsapp, setTelefoneWhatsapp] = useState("")
    const [carregandoCredenciais, setCarregandoCredenciais] = useState(false)
    const [temSessaoCriptografada, setTemSessaoCriptografada] = useState(false)

    const [templatesLista, setTemplatesLista] = useState([])
    const [carregandoTemplates, setCarregandoTemplates] = useState(false)
    const [templateSelectKey, setTemplateSelectKey] = useState("")

    const [paramHeader, setParamHeader] = useState([])
    const [paramBody, setParamBody] = useState([])

    const [enviando, setEnviando] = useState(false)
    const [progresso, setProgresso] = useState({ atual: 0, total: 0 })

    const templateSelecionadoObj = useMemo(() => {
        if (!templateSelectKey) return null
        return templatesLista.find(t => templateOptionKey(t) === templateSelectKey) || null
    }, [templateSelectKey, templatesLista])

    const flagsTemplate = useMemo(
        () =>
            templateSelecionadoObj
                ? countTemplateTextParameters(templateSelecionadoObj)
                : { headerCount: 0, bodyCount: 0, headerIsMedia: false },
        [templateSelecionadoObj]
    )

    const loadTemplatesList = useCallback(
        async (wabaid, token) => {
            const wa = String(wabaid || "").trim()
            const tk = String(token || "").trim()
            if (!wa || !tk) return

            setCarregandoTemplates(true)
            const r = await fetchWhatsappMessageTemplates(wa, tk)
            setCarregandoTemplates(false)

            if (!r.ok) {
                const msg =
                    r.data?.error?.message || r.data?.error?.error_user_msg || `Erro ${r.status}`
                enqueueSnackbar(`Templates: ${msg}`, { variant: "error" })
                setTemplatesLista([])
                setTemplateSelectKey("")
                return
            }

            const approved = r.templates.filter(
                t => String(t.status || "").toUpperCase() === "APPROVED"
            )
            approved.sort((a, b) =>
                `${a.name} ${templateLanguageCode(a)}`.localeCompare(
                    `${b.name} ${templateLanguageCode(b)}`,
                    "pt-BR"
                )
            )
            setTemplatesLista(approved)

            if (approved.length === 0) {
                setTemplateSelectKey("")
                enqueueSnackbar("Nenhum template aprovado encontrado na conta.", {
                    variant: "info",
                })
                return
            }

            setTemplateSelectKey(prev => {
                if (prev && approved.some(t => templateOptionKey(t) === prev)) return prev
                return templateOptionKey(approved[0])
            })
        },
        [enqueueSnackbar]
    )

    useEffect(() => {
        if (!templateSelecionadoObj) {
            setParamHeader([])
            setParamBody([])
            return
        }
        const { headerCount, bodyCount } = countTemplateTextParameters(templateSelecionadoObj)
        setParamHeader(Array(Math.max(0, headerCount)).fill(""))
        setParamBody(Array(Math.max(0, bodyCount)).fill(""))
    }, [templateSelecionadoObj])

    useEffect(() => {
        if (!userId || !perfilPodeWhatsappTemplates) return
        if (!idEstabelecimento) return
        const chave = localStorage.getItem("chaveSistema")
        if (!chave) return

        let cancelled = false

        ;(async () => {
            const fromSession = await loadWhatsappOfficialSession(
                buildWhatsappSessionContextKey(chave, userId, idEstabelecimento)
            )
            if (cancelled) return

            if (fromSession) {
                tokenRef.current = fromSession.token
                setContaWhatsapp(fromSession.conta || "")
                setWabaidGraph(fromSession.wabaid)
                setTelefoneWhatsapp(fromSession.telefone || "")
                setTemSessaoCriptografada(true)
                await loadTemplatesList(fromSession.wabaid, fromSession.token)
                return
            }

            setCarregandoCredenciais(true)
            const res = await buscarTokenWhatsappOficial(idEstabelecimento, chave)
            setCarregandoCredenciais(false)
            if (cancelled) return

            if (res.ok) {
                await saveWhatsappOfficialSession({
                    token: res.token,
                    wabaid: res.wabaid,
                    conta: res.conta || "",
                    telefone: res.telefone || "",
                    contextKey: buildWhatsappSessionContextKey(chave, userId, idEstabelecimento),
                })
                tokenRef.current = res.token
                setContaWhatsapp(res.conta || "")
                setWabaidGraph(res.wabaid)
                setTelefoneWhatsapp(res.telefone || "")
                setTemSessaoCriptografada(true)
                await loadTemplatesList(res.wabaid, res.token)
            } else {
                enqueueSnackbar(res.mensagem, { variant: "warning" })
                setTemSessaoCriptografada(false)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [
        userId,
        perfilPodeWhatsappTemplates,
        idEstabelecimento,
        enqueueSnackbar,
        loadTemplatesList,
    ])

    if (userId && !perfilPodeWhatsappTemplates) {
        return <Redirect to="/servicos" />
    }

    if (!userId) {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                minHeight="100vh"
            >
                <CircularProgress />
            </Box>
        )
    }

    const desconectar = () => {
        try {
            tokenRef.current = null
            clearWhatsappOfficialSession()
            localStorage.removeItem("chaveSistema")
            localStorage.removeItem("dataUltimoServico")
            window.name = ""
            history.push("/login")
        } catch (e) {
            console.error(e)
        }
    }

    const updateHeaderParam = (index, value) => {
        setParamHeader(prev => {
            const next = [...prev]
            next[index] = value
            return next
        })
    }

    const updateBodyParam = (index, value) => {
        setParamBody(prev => {
            const next = [...prev]
            next[index] = value
            return next
        })
    }

    const handleEnviar = async () => {
        let token = tokenRef.current != null ? String(tokenRef.current).trim() : ""
        if (!token) {
            try {
                const chaveAtual = localStorage.getItem("chaveSistema")
                const sess = await loadWhatsappOfficialSession(
                    buildWhatsappSessionContextKey(chaveAtual, userId, idEstabelecimento)
                )
                if (sess?.token) {
                    tokenRef.current = sess.token
                    token = sess.token.trim()
                }
            } catch {
                /* ignore */
            }
        }
        if (!token) token = getWhatsappGraphToken()

        if (!token) {
            enqueueSnackbar(
                "Sem token Graph. Recarregue a página ou verifique a configuração no servidor.",
                { variant: "error" }
            )
            return
        }

        if (!templateSelecionadoObj) {
            enqueueSnackbar("Selecione um template.", { variant: "warning" })
            return
        }

        if (flagsTemplate.headerIsMedia) {
            enqueueSnackbar(
                "Este template usa cabeçalho de mídia (imagem/vídeo/documento); envio não suportado por esta tela.",
                { variant: "error" }
            )
            return
        }

        const linhas = telefonesRaw
            .split(/\r?\n/)
            .map(l => l.trim())
            .filter(Boolean)

        const numeros = []
        const invalidas = []
        for (const linha of linhas) {
            const n = normalizeWhatsappPhone(linha)
            if (n) numeros.push(n)
            else invalidas.push(linha)
        }

        if (numeros.length === 0) {
            enqueueSnackbar("Informe ao menos um telefone válido (um por linha).", {
                variant: "warning",
            })
            return
        }

        if (invalidas.length > 0) {
            enqueueSnackbar(
                `${invalidas.length} linha(s) ignorada(s) por formato inválido.`,
                { variant: "info" }
            )
        }

        const headerTexts =
            flagsTemplate.headerCount > 0 ? paramHeader.slice(0, flagsTemplate.headerCount) : []
        const bodyTexts =
            flagsTemplate.bodyCount > 0 ? paramBody.slice(0, flagsTemplate.bodyCount) : []

        const contaParaEnvio =
            contaWhatsapp.trim() ||
            String(process.env.REACT_APP_WHATSAPP_GRAPH_WABA_ID || "").trim()
        if (!contaParaEnvio) {
            enqueueSnackbar(
                "Campo conta indisponível para o envio (URL /messages). Atualize as credenciais.",
                { variant: "error" }
            )
            return
        }

        setEnviando(true)
        setProgresso({ atual: 0, total: numeros.length })

        let sucesso = 0
        let falhas = 0

        const payloadTemplate = {
            name: templateSelecionadoObj.name,
            languageCode: templateLanguageCode(templateSelecionadoObj),
            headerTexts,
            bodyTexts,
        }

        for (let i = 0; i < numeros.length; i++) {
            const to = numeros[i]
            setProgresso({ atual: i + 1, total: numeros.length })

            try {
                const result = await sendWhatsappTemplateMessage(
                    to,
                    payloadTemplate,
                    token,
                    contaParaEnvio
                )

                if (result.ok) {
                    sucesso++
                } else {
                    falhas++
                    console.warn("WhatsApp Graph", to, result.status, result.data)
                }
            } catch (err) {
                falhas++
                console.error(err)
            }

            if (i < numeros.length - 1) {
                await new Promise(r => setTimeout(r, 350))
            }
        }

        setEnviando(false)
        setProgresso({ atual: 0, total: 0 })

        if (falhas === 0) {
            enqueueSnackbar(`Enviado com sucesso para ${sucesso} número(s).`, {
                variant: "success",
            })
        } else {
            enqueueSnackbar(
                `Concluído: ${sucesso} ok, ${falhas} falha(s). Verifique o console e a API.`,
                { variant: falhas === numeros.length ? "error" : "warning" }
            )
        }
    }

    return (
        <div className={classes.root}>
            <Cabecalho pagina={"Envio de template (grupo)"} cor={"#464646"} desconectar={desconectar} />

            <Paper className={classes.paper} elevation={2}>
                <Typography variant="h6" gutterBottom>
                    Envio de templates WhatsApp
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                    Um telefone por linha (com ou sem DDI). Token Graph fica apenas em sessão
                    criptografada (não exibido).
                </Typography>

                <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                    {(carregandoCredenciais || carregandoTemplates) && (
                        <CircularProgress size={24} />
                    )}
                    <Typography variant="body2" color="textSecondary">
                        {temSessaoCriptografada
                            ? "Token armazenado criptografado nesta aba (sessionStorage)."
                            : "Sem credenciais em sessão."}
                    </Typography>
                </Box>

                <Box className={classes.credBox}>
                    <Typography variant="subtitle2" gutterBottom>
                        Dados da configuração WhatsApp (API interna)
                    </Typography>
                    <Typography variant="body2" component="div" gutterBottom>
                        <strong>Conta</strong> — <code>{contaWhatsapp || "—"}</code>
                    </Typography>
                    <Typography variant="body2" component="div" gutterBottom>
                        <strong>wabaid</strong> — <code>{wabaidGraph || "—"}</code>
                    </Typography>
                    <Typography variant="body2" component="div">
                        <strong>Telefone</strong> — <code>{telefoneWhatsapp || "—"}</code>
                    </Typography>
                </Box>

                <TextField
                    className={classes.field}
                    label="Lista de telefones"
                    placeholder={"5531990744897\n31990744897"}
                    multiline
                    rows={8}
                    variant="outlined"
                    fullWidth
                    value={telefonesRaw}
                    onChange={e => setTelefonesRaw(e.target.value)}
                    disabled={enviando}
                />

                <FormControl variant="outlined" className={classes.field} disabled={enviando}>
                    <InputLabel id="template-select-label">Template (aprovados)</InputLabel>
                    <Select
                        labelId="template-select-label"
                        label="Template (aprovados)"
                        value={templateSelectKey}
                        onChange={e => setTemplateSelectKey(e.target.value)}
                    >
                        {templatesLista.length === 0 ? (
                            <MenuItem value="" disabled>
                                {carregandoTemplates ? "Carregando…" : "Nenhum template"}
                            </MenuItem>
                        ) : (
                            templatesLista.map(t => (
                                <MenuItem key={templateOptionKey(t)} value={templateOptionKey(t)}>
                                    {t.name} ({templateLanguageCode(t)})
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>

                {flagsTemplate.headerIsMedia && (
                    <div className={classes.warn}>
                        Este template possui cabeçalho de mídia. O disparo por aqui só cobre templates
                        com cabeçalho de texto ou sem cabeçalho com variáveis de mídia.
                    </div>
                )}

                {flagsTemplate.headerCount > 0 && (
                    <Typography variant="subtitle2" gutterBottom>
                        Parâmetros do cabeçalho ({flagsTemplate.headerCount})
                    </Typography>
                )}
                {paramHeader.map((val, i) => (
                    <TextField
                        key={`h-${i}`}
                        className={classes.field}
                        label={`Cabeçalho {{${i + 1}}}`}
                        variant="outlined"
                        fullWidth
                        value={val}
                        onChange={e => updateHeaderParam(i, e.target.value)}
                        disabled={enviando}
                    />
                ))}

                {flagsTemplate.bodyCount > 0 && (
                    <Typography variant="subtitle2" gutterBottom style={{ marginTop: 8 }}>
                        Parâmetros do corpo ({flagsTemplate.bodyCount})
                    </Typography>
                )}
                {paramBody.map((val, i) => (
                    <TextField
                        key={`b-${i}`}
                        className={classes.field}
                        label={`Corpo {{${i + 1}}}`}
                        variant="outlined"
                        fullWidth
                        value={val}
                        onChange={e => updateBodyParam(i, e.target.value)}
                        disabled={enviando}
                    />
                ))}

                <Box className={classes.actions}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleEnviar}
                        disabled={
                            enviando ||
                            flagsTemplate.headerIsMedia ||
                            !templateSelectKey ||
                            carregandoTemplates
                        }
                    >
                        Enviar
                    </Button>
                    {enviando && (
                        <>
                            <CircularProgress size={24} />
                            <Typography variant="body2" color="textSecondary">
                                {progresso.atual} / {progresso.total}
                            </Typography>
                        </>
                    )}
                </Box>

                <Typography className={classes.hint} component="div">
                    Credenciais: <code>GET comum/whatsapp/oficial/`{"{"}estabelecimento{"}"}`</code> (
                    header <code>authorization</code>). Templates:{" "}
                    Listagem: <code>GET …/v24.0/`{"{"}wabaid{"}"}`/message_templates</code>. Envio:{" "}
                    <code>POST …/v24.0/`{"{"}conta{"}"}`/messages</code>. Opcional:{" "}
                    <code>REACT_APP_WHATSAPP_GRAPH_TOKEN</code> /{" "}
                    <code>REACT_APP_WHATSAPP_GRAPH_WABA_ID</code> / proxy de URL.
                </Typography>
            </Paper>
        </div>
    )
}
